import { useCallback, useMemo } from 'react'
import type * as Y from 'yjs'
import { Block } from '@ttab/elephant-api/newsdoc'
import type { TBSpellingError } from '@ttab/textbit'
import type { YDocument } from '@/modules/yjs/hooks'
import { useYValue } from '@/modules/yjs/hooks'
import { getValueByYPath, toYStructure } from '@/shared/yUtils'
import { useActiveAuthor } from '@/hooks/useActiveAuthor'

export const ACCEPTED_SPELLING_TYPE = 'core/accepted-spelling'

export interface AcceptedSpelling {
  /** Whether this word has been accepted in this document. */
  isAccepted: (error: TBSpellingError) => boolean
  /** Accept it here. */
  accept: (text: string) => void
  /** Take that back. */
  remove: (text: string) => void
}

export function useAcceptedSpelling(ydoc: YDocument<Y.Map<unknown>>): AcceptedSpelling {
  const [accepted, setAccepted] = useYValue<Block[] | undefined>(ydoc.ele, `meta.${ACCEPTED_SPELLING_TYPE}`)
  const author = useActiveAuthor({ full: false })

  // Folded to lower case, so the predicate does not walk the blocks for every
  // marked word on every render, and so a word accepted mid sentence is also
  // accepted where it starts one. What is stored keeps the case it was
  // accepted with, that is a record of what someone said; matching is the
  // lenient half.
  const acceptedTexts = useMemo(
    () => new Set(
      (accepted || [])
        .map((block) => block.value?.toLowerCase())
        .filter(Boolean)
    ),
    [accepted]
  )

  const isAccepted = useCallback(
    (error: TBSpellingError) => acceptedTexts.has(error.text.toLowerCase()),
    [acceptedTexts]
  )

  const accept = useCallback((text: string) => {
    const value = text.trim()

    if (!value || acceptedTexts.has(value.toLowerCase())) {
      return
    }

    const block = Block.create({
      type: ACCEPTED_SPELLING_TYPE,
      value,
      data: {
        created: new Date().toISOString()
      },
      // Whoever accepted it, as a link to their author document the way every
      // other reference to a person in a document is made. Left out rather
      // than guessed when there is no author to point at. The name has to be
      // there too: the schema requires it, so writing a nameless link would
      // fail the save rather than degrade, and losing the acceptance over it
      // is worse than not recording who made it.
      links: author?.name
        ? [Block.create({
            type: 'core/author',
            rel: 'creator',
            uuid: author.id,
            title: author.name
          })]
        : []
    })

    // Append to the live array. Writing a whole new value for the key is last
    // writer wins on a Y.Map, so it would undo whatever anyone else accepted or
    // took back at the same moment, and rebuild every surviving block besides.
    const [yAccepted] = getValueByYPath<Y.Array<unknown>>(ydoc.ele, `meta.${ACCEPTED_SPELLING_TYPE}`, true)

    if (yAccepted) {
      yAccepted.push([toYStructure(block)])
      return
    }

    // Nothing to preserve when nothing has been accepted in the document yet.
    setAccepted([block])
  }, [acceptedTexts, author, setAccepted, ydoc.ele])

  /**
   * Take an acceptance back, so the word is marked as a correction again.
   * Matches the way accepting does, without regard to case.
   */
  const remove = useCallback((text: string) => {
    const value = text.trim().toLowerCase()
    const [yAccepted] = getValueByYPath<Y.Array<unknown>>(ydoc.ele, `meta.${ACCEPTED_SPELLING_TYPE}`, true)

    if (!value || !yAccepted?.length) {
      return
    }

    // Delete the elements rather than writing a shorter list, for the same
    // reason. Matched against the document as it is now, not the render this
    // was called from, and from the back so the indexes hold as they go.
    const doomed = yAccepted
      .toArray()
      .map((block, index) => ({ index, value: (block as Y.Map<unknown>).get('value') }))
      .filter(({ value: v }) => typeof v === 'string' && v.toLowerCase() === value)

    for (const { index } of doomed.reverse()) {
      yAccepted.delete(index, 1)
    }
  }, [ydoc.ele])

  return { isAccepted, accept, remove }
}
